<script setup async>
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import CompetitionQuestionsPrompt from "@/components/competitions/CompetitionQuestionsPrompt.vue";
import {ref, onMounted, onBeforeUnmount} from "vue";
import OutstandingFeeBanner from "~/components/payments/OutstandingFeeBanner.vue";
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useValidation} from "~/composables/useValidation";
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ClubChip from "~/components/clubs/ClubChip.vue";
import LeagueCycleView from "~/components/leagues/LeagueCycleView.vue";
import NotPublishedNotice from "~/components/NotPublishedNotice.vue";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import JoinRequestsCard from "~/components/join/JoinRequestsCard.vue";
import {createError} from "nuxt/app";
import {useFormData} from "~/composables/useFormData";

const {notify, addListener, removeListener, removeLocalListenersWithEvent, removeLocalListeners} = useListenerService();
const {isGuid} = useValidation();
definePageMeta({
  name: 'LeagueDetail',
  path: '/leagues/:leagueId/:cycleId?',
});

const {item: league, getItem: getLeague, itemPending: leagueLoading, hasItemErrors} = useCrudApi("api/v2/leagues");

const route = useRoute();
const router = useRouter();
const leagueId = ref(route.params.leagueId);
const cycleId = ref(route.params.cycleId);
const selectedCycleId = ref(cycleId.value);
const loadData = async () => {
  await getLeague(leagueId.value);
  if (hasItemErrors.value) {
    throw createError({
      statusCode: 404, // or another relevant status code
      message: 'Box league not found. Has it been deleted?'
    });
  }
  //selected cycle id must be within the cycles of the league
  if (selectedCycleId.value != null && league.value.cycles.filter(_ => _.id === selectedCycleId.value).length === 0) {
    selectedCycleId.value = null;
  }
  //If no cycle is selected, select the active cycle
  // The API sends "no active cycle" as the all-zeros GUID, not null — treat it as none,
  // or the fallback below never runs and the page shows an empty cycle picker.
  const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
  const activeId = league.value.activeCycleId && league.value.activeCycleId !== EMPTY_GUID
      ? league.value.activeCycleId : null;
  if (selectedCycleId.value == null && activeId != null) {
    selectedCycleId.value = activeId;
  }
  // No active cycle → fall back to the most recent PUBLISHED cycle (by end date,
  // then start date) so the page shows a cycle rather than an empty selector.
  // Archived (deleted) cycles are never auto-selected.
  if (selectedCycleId.value == null) {
    const published = (league.value.cycles ?? []).filter(c => c.published && !c.archivedAt);
    if (published.length) {
      const mostRecent = [...published].sort((a, b) =>
          (b.endDate || b.startDate || '').localeCompare(a.endDate || a.startDate || ''))[0];
      selectedCycleId.value = mostRecent.id;
    }
  }
};

await loadData();

const {setMeta} = useHeadService();
setMeta(
    league.value?.clubName + ' / ' + league.value?.name + ' on Tap Leagues',
    league?.value?.clubName + ' / ' + league?.value?.name + ' on Tap Leagues. Find out more about ' + league?.value?.name + ' and view matches and results.'
);
const {user, isAuthenticated, isAdminOfClub, isMemberOfClub} = useAuthService();
const {leaveLeagueCycleDivision, leavePending} = useLeaveEntity();

const hasActiveCycle = computed(() => league.value && league.value.activeCycleId != null);
const hasPublishedCycles = computed(() => league.value && league.value.cycles.filter(_ => _.published && !_.archivedAt).length > 0);

// The league itself has been deleted (archived) - show the deleted notice instead of content.
const leagueArchived = computed(() => !!league.value?.archivedAt);
usePrivatePage(computed(() => !!league.value?.privateToViewer));

const selectedCycle = computed(() =>
    league.value?.cycles?.find(c => c.id === selectedCycleId.value) ?? null);

// Respect the cycle's published flag even on a direct /leagues/{id}/{cycleId} URL.
const selectedCycleUnpublished = computed(() => !!selectedCycle.value && !selectedCycle.value.published);

// A direct /leagues/{id}/{cycleId} URL can point at a deleted (archived) cycle.
const selectedCycleArchived = computed(() => !!selectedCycle.value?.archivedAt);

// Check if current user is a member of the selected league cycle
const isInLeagueCycle = computed(() => {
  if (!isAuthenticated.value || !user.value?.playerId) return false;
  if (!selectedCycle.value?.leagueCycleDivisions) return false;
  return selectedCycle.value.leagueCycleDivisions.some(div =>
      div.teams?.some(t => t.players?.some(p => p.id === user.value.playerId))
  );
});

// Find the user's league cycle division ID
const userLeagueCycleDivisionId = computed(() => {
  if (!user.value?.playerId || !selectedCycle.value?.leagueCycleDivisions?.length) return null;

  for (const division of selectedCycle.value.leagueCycleDivisions) {
    if (!division.teams?.length) continue;
    const isInDivision = division.teams.some(t =>
        t.players?.some(p => p.id === user.value.playerId)
    );
    if (isInDivision) return division.id;
  }
  return null;
});

// Filter join link through the same visibility rules as the other competition pages
const visibleJoinLink = computed(() => {
  // LeagueCycleListResponse carries visibleInviteLink (already members-only gated server-side); joinLink exists only on ladders
  const raw = selectedCycle.value?.visibleInviteLink;
  if (!raw) return null;
  if (isAdminOfClub(league.value?.clubId)) return raw;

  // Non-admins require: visible (0), active, and scope-allowed
  const scopeAllowed = raw.inviteScope !== 1 || isMemberOfClub(league.value?.clubId);
  return raw.inviteEntityPageSetting !== 1 && raw.isActive && scopeAllowed
      ? raw : null;
});

const {t} = useI18n()
const {cycleItemTitle} = useFormData();

const showLeaveLeagueDialog = ref(false);

const handleLeaveLeague = async () => {
  if (!userLeagueCycleDivisionId.value) return;

  try {
    await leaveLeagueCycleDivision(userLeagueCycleDivisionId.value);
    showLeaveLeagueDialog.value = false;
    // Refresh the league data
    await loadData();
  } catch (error) {
    console.error('Failed to leave league cycle division:', error);
    // Keep dialog open on error so user can see the error message
  }
};

const titleActions = computed(() => {
  const actions = [];

  if (league.value && isAdminOfClub(league.value.clubId)) {
    actions.push({
      title: 'Edit box league',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubLeagueSettings', params: {clubId: league.value.clubId, leagueId: league.value.id}}),
      color: 'info',
    });
  }

  // "View fixtures" now lives in the LeagueCycleView action strip (as a .as tl-action),
  // for consistency with the tournament page — no longer a top-right title action.

  if (isInLeagueCycle.value) {
    actions.push({
      title: 'Leave box league',
      icon: 'mdi-exit-to-app',
      click: () => showLeaveLeagueDialog.value = true,
      color: 'error',
    });
  }

  return actions;
});
watch(selectedCycleId, (val) => {
  router.replace({name: 'LeagueDetail', params: {leagueId: leagueId.value, cycleId: val}});
});

watch(() => cycleId.value, (val) => {
  selectedCycleId.value = val;
});

const onFixtureUpdated = (item) => {
  router.replace({name: 'LeagueDetail', params: {leagueId: leagueId.value, cycleId: selectedCycleId.value}});
};

onMounted(() => {
  addListener('fixtureUpdated', onFixtureUpdated);
  // Approving/rejecting a join request updates the cycle's accessApplicationCount.
  addListener('requestsUpdated', () => loadData());
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
  removeLocalListenersWithEvent('requestsUpdated');
});


</script>

<template>
  <base-page>
    <template v-if="leagueLoading">
      <v-container class="large">
        <v-row>
          <v-col cols="12">
            <v-skeleton-loader height="50"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader type="list-item-avatar-two-line"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader height="50"/>
          </v-col>
          <v-col cols="12">
            <v-skeleton-loader type="table"/>
          </v-col>

        </v-row>
      </v-container>
    </template>
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <template v-else-if="league && league.privateToViewer">
      <v-container class="large">
        <frontend-header :league="league" :club="league.club ?? (league.clubId ? {id: league.clubId, slug: league.clubId, name: league.clubName} : null)" :title="league.name" title-icon="mdi-table">
          <template #subTitle>
            <club-chip v-if="league.club" :club="league.club"></club-chip>
            <span v-else-if="league.clubName" class="tl-chip">{{ league.clubName }}</span>
          </template>
        </frontend-header>
        <div class="tl-action-strip tl-action-strip--solo my-5">
          <short-link-share-wrapper>
            <template #default="{ click, loading: shareLoading }">
              <button class="as" @click="click" :disabled="shareLoading">
                <v-icon size="17" icon="mdi-share-variant"/>Share
              </button>
            </template>
          </short-link-share-wrapper>
        </div>
        <private-notice reason="competition" entity="box league" :name="league.name" :club-name="league.clubName" :join-slug="league.visibleInviteLink?.slug"
                      :facts="competitionFacts('league', league, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
      </v-container>
    </template>
    <template v-else-if="league && leagueArchived">
      <!-- Deleted (archived) league - show the notice instead of any content -->
      <v-container class="large">
        <archived-notice entity="box league" :name="league.name"/>
      </v-container>
    </template>
    <template v-else-if="league">
      <v-container class="large" >
        <outstanding-fee-banner v-if="isAuthenticated && selectedCycleId" :source-id="selectedCycleId"/>
        <frontend-header
            :club="league.club"
            :league="league"
            :title="league.name"
            title-icon="mdi-table"
            :actions="titleActions"
        >
          <template #subTitle>
            <club-chip :club="league.club"></club-chip>
          </template>
        </frontend-header>
      </v-container>
      <v-container class="large" >
        <!-- Direct link to a deleted (archived) cycle -->
        <archived-notice v-if="selectedCycleArchived" entity="cycle" :name="selectedCycle.name"/>

        <!-- Direct link to an unpublished cycle isn't viewable publicly -->
        <not-published-notice v-else-if="selectedCycleUnpublished" entity="cycle"/>

        <template v-else>
          <v-alert v-if="league.cycles.filter(_ => _.published && !_.archivedAt).length === 0" color="info" variant="tonal"
                   icon="mdi-information" class="mb-4">
            This box league has no cycles
          </v-alert>
          <v-alert v-else-if="league.activeCycleId == null" color="info" variant="tonal" icon="mdi-information" class="mb-4">
            This box league has no active cycles
          </v-alert>

          <template v-if="league.cycles.filter(_ => _.published && !_.archivedAt).length > 0">
            <div class="cycle-field mb-5">
              <div class="tl-label mb-1">
                {{ league.activeCycleId != null ? 'Cycle' : 'Cycle (no active cycle, select one to view)' }}
              </div>
              <v-select
                  density="compact"
                  variant="outlined"
                  bg-color="white"
                  class="cycle-select"
                  :hide-details="true"
                  item-value="id"
                  :item-title="cycleItemTitle"
                  :items="league.cycles.filter(_ => _.published && !_.archivedAt)"
                  v-model="selectedCycleId"
              >
              </v-select>
            </div>
            <template v-if="hasPublishedCycles && isGuid(selectedCycleId)">
              <!-- Admin invite CTA — per selected cycle; opens the join-links dialog -->
              <join-settings-card
                  v-if="isAdminOfClub(league.clubId) && selectedCycle"
                  :key="selectedCycleId"
                  class="mb-4"
                  :reference-type="2"
                  :reference-id="selectedCycleId"
                  :club-id="league.clubId"
                  :entity-name="`${league.name} / ${selectedCycle.name}`"
                  @changed="loadData"
              />
              <!-- Pending join requests for the selected cycle (admins only) -->
              <join-requests-card
                  v-if="isAdminOfClub(league.clubId) && selectedCycle"
                  :count="selectedCycle.accessApplicationCount ?? 0"
                  :filters="{ leagueCycleIds: [selectedCycleId] }"
              />
              <league-cycle-view
                  :id="selectedCycleId"
                  :is-in-league-cycle="isInLeagueCycle"
                  :information-html="league.informationHtml"
                  :visible-join-link="visibleJoinLink"
                  @changed="loadData"></league-cycle-view>
            </template>
          </template>
        </template>
      </v-container>

      <!-- Leave League Confirmation Dialog -->
      <v-dialog v-model="showLeaveLeagueDialog" max-width="390">
        <v-card class="bg-white d-flex flex-column pa-0">
          <v-toolbar>
            <v-toolbar-items>
              <v-btn
                  color="error"
                  variant="flat"
                  @click="showLeaveLeagueDialog = false"
              >
                <v-icon color="white" icon="mdi-close"></v-icon>
              </v-btn>
            </v-toolbar-items>
            <v-toolbar-title>Leave Box League</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pa-5 text-text" style="font-size: 1.25rem;">
            Are you sure you want to leave this box league? You will need to request access again to rejoin.
          </v-card-text>
          <v-card-actions class="pa-0 border-t pa-4 elevation-5">
            <v-btn size="large" variant="flat" color="error" @click="showLeaveLeagueDialog = false">
              Cancel
            </v-btn>
            <v-spacer/>
            <v-btn
                size="large"
                class="px-5"
                color="primary"
                variant="flat"
                :loading="leavePending"
                @click="handleLeaveLeague"
            >
              <v-icon color="white" size="16" class="mr-2" icon="mdi-check"></v-icon>
              Leave Box League
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <competition-questions-prompt competition-type="league" :competition-id="league?.id"/>
  </base-page>
</template>

<style scoped lang="scss">
/* cycle picker styled like the kit field (mockups/redesign/league.html) */
.cycle-field {
  max-width: 420px;
}

.cycle-select :deep(.v-field) {
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.cycle-select :deep(.v-field__outline__start),
.cycle-select :deep(.v-field__outline__end) {
  border-color: rgb(var(--v-theme-borderLight));
  opacity: 1;
}

.cycle-select :deep(.v-field__outline__start) {
  border-radius: 12px 0 0 12px;
}

.cycle-select :deep(.v-field__outline__end) {
  border-radius: 0 12px 12px 0;
}
</style>
